/* eslint-disable @typescript-eslint/no-explicit-any */
import 'cropperjs/dist/cropper.css'

import { ChangeEvent, createRef, useRef, useState } from 'react'
import Cropper, { ReactCropperElement } from 'react-cropper'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { dataURItoFile } from '../../helpers/dataURItoFile.ts'
import { Button } from '../Button'
import {
  deleteUserAvatar,
  uploadUserAvatar,
} from '../../services/userProfile.ts'
import { getCurrentUser } from '../../store/actions/authActions.ts'
import { useAppDispatch } from '../../store/index.ts'

interface AvatarFileProps {
  avatarUrl: string | null
  type?: 'account' | 'design'
}

export const AvatarFile = ({ avatarUrl, type }: AvatarFileProps) => {
  const { setValue, watch } = useFormContext<DesignFormFields>()
  const avatarFormField = watch().avatar
  const isUploaded = typeof avatarFormField === 'string'
  const dispatch = useAppDispatch()

  // Image data
  const [image, setImage] = useState<string>('')
  const [cropData, setCropData] = useState<string>('')

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cropperRef = createRef<ReactCropperElement>()

  const { t } = useTranslation()

  const handleChange = (
    e: ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>,
  ) => {
    e.preventDefault()
    if (cropData) {
      setCropData('')
    }

    let files: FileList | null

    if ('dataTransfer' in e) {
      files = e.dataTransfer.files
    } else {
      files = e.target.files
    }

    if (!files || files.length === 0) {
      toast.error('No file selected.')
      return
    }

    const file = files[0]
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      toast.error(t('invalidFile'))
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      setImage(reader.result as string)
    }
    reader.readAsDataURL(file)
  }

  const handleCrop = () => {
    if (cropperRef.current?.cropper) {
      const canvas = cropperRef.current.cropper.getCroppedCanvas()
      if (canvas) {
        const croppedImage = canvas.toDataURL()
        setCropData(croppedImage)

        const file = dataURItoFile(croppedImage, 'image.jpg')
        setValue('avatar', file)
      }
    }
  }



  const handleDeleteImage = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.stopPropagation()
    setCropData('')
    setImage('')
    setValue('avatar', '')
    if (type === 'account') {
      deleteUserAvatar().then(() => {
        dispatch(getCurrentUser())
      })
    }
  }

  const handleSaveImg = async () => {
    if (cropData) {
      const file = dataURItoFile(cropData, 'image.jpg')
      const formData = new FormData()
      formData.append('file', file)

      try {
        const response = await uploadUserAvatar(formData)
        if (response.status === 200) {
          dispatch(getCurrentUser())
        }
      } catch (error) {
        console.error('Error uploading avatar:', error)
        toast.error('Failed to upload avatar.')
      }
    }
  }

  return (
    <div className="mb-4">
      <div className="relative mx-auto">
        <div className={`flex w-full items-center justify-center`}>
          <label
            className="flex h-[100px] w-[100px] cursor-pointer flex-col
          items-center justify-center overflow-hidden rounded-full bg-[#c2c2c2]"
          >
            {/* Visual fallback avatar */}
            {avatarUrl ? (
              <img
                className="pointer-events-none h-full w-full object-cover"
                src={avatarUrl}
              />
            ) : (
              <div className="pointer-events-none flex flex-col items-center justify-center">
                <span className="text-5xl text-secondary">A</span>
              </div>
            )}

            {/* Hidden file input */}
            {/* <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleChange}
            /> */}
          </label>
        </div>

        {/* Cropper */}
        <div
          className={`${!cropData && image ? 'opacity-1' : 'opacity-0'} pointer-events-none absolute left-1/2 top-1/2 z-20 mx-auto flex h-[100px] w-[100px] -translate-x-1/2 -translate-y-1/2 cursor-pointer flex-col
          items-center justify-center overflow-hidden rounded-full bg-[#c2c2c2] opacity-0`}
        >
          <Cropper
            ref={cropperRef}
            initialAspectRatio={1}
            preview=".img-preview"
            src={image}
            viewMode={1}
            minCropBoxHeight={10}
            minCropBoxWidth={10}
            background={false}
            responsive={true}
            autoCropArea={1}
            checkOrientation={false}
            guides={true}
            crop={handleCrop}
          />
        </div>

        {/* New image file */}
        {cropData && image && (
          <div
            className={`${cropData && image ? 'opacity-1' : 'opacity-0'} pointer-events-none absolute left-1/2  top-1/2 z-10 mx-auto flex h-[100px] w-[100px] -translate-x-1/2 -translate-y-1/2 cursor-pointer
          flex-col items-center justify-center overflow-hidden rounded-full bg-[#c2c2c2]`}
          >
            <img className="relative h-auto w-full" src={cropData} />
          </div>
        )}
      </div>

      {/* Delete image preview  */}
      {image && !isUploaded ? (
        <div className="mt-5 flex justify-center gap-4">
          <Button
            type="button"
            style={{ float: 'right' }}
            onClick={(e) => handleDeleteImage(e)}
            text={t('delete')}
            className="h-[36px] w-full border border-[#2b2b2b] bg-white px-2 text-sm text-black hover:bg-[#407BFF] hover:text-white lg:w-[160px]"
          />
          {type === 'account' ? (
            <Button
              onClick={handleSaveImg}
              text="Save"
              type="button"
              className="h-[36px] w-full border border-[#2b2b2b] bg-white px-2 text-sm text-black hover:bg-[#407BFF] hover:text-white lg:w-[160px]"
            />
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
